import type { ClientMessage, ServerMessage } from '@shared/types/messages';

export type SocketStatus = 'connecting' | 'connected' | 'reconnecting' | 'closed';

export interface RoomSocketHandlers {
  onMessage: (message: ServerMessage) => void;
  onStatus: (status: SocketStatus, detail?: { code?: number; reason?: string }) => void;
}

const KEEPALIVE_MS = 25_000;
const MAX_BACKOFF_MS = 10_000;

/**
 * 방 WebSocket 클라이언트.
 * - 연결 직후 auth 메시지로 인증
 * - 4000번대 종료 코드(방 종료/인증 실패/강퇴 등)는 재접속하지 않음
 * - 그 외 끊김은 지수 백오프로 자동 재접속
 */
export class RoomSocket {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private closedByUser = false;
  private keepalive: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly roomId: string,
    private readonly participantId: string,
    private readonly token: string,
    private readonly handlers: RoomSocketHandlers,
  ) {}

  connect(): void {
    this.closedByUser = false;
    this.open();
  }

  send(message: ClientMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(message));
    return true;
  }

  close(): void {
    this.closedByUser = true;
    this.cleanupTimers();
    if (this.ws) {
      try {
        this.ws.close(1000, 'client closed');
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  private url(): string {
    const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
    return `${protocol}://${location.host}/api/rooms/${encodeURIComponent(this.roomId)}/ws`;
  }

  private open(): void {
    this.handlers.onStatus(this.attempts === 0 ? 'connecting' : 'reconnecting');
    const ws = new WebSocket(this.url());
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) return;
      ws.send(JSON.stringify({ type: 'auth', participantId: this.participantId, token: this.token } satisfies ClientMessage));
      this.keepalive = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, KEEPALIVE_MS);
    };

    ws.onmessage = (event) => {
      if (this.ws !== ws) return;
      if (typeof event.data !== 'string' || event.data === 'pong') return;
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data) as ServerMessage;
      } catch {
        return;
      }
      if (message.type === 'room:joined') {
        this.attempts = 0;
        this.handlers.onStatus('connected');
      }
      this.handlers.onMessage(message);
    };

    ws.onclose = (event) => {
      if (this.ws !== ws) return;
      this.cleanupTimers();
      this.ws = null;
      const fatal = event.code >= 4000 && event.code < 4100;
      if (this.closedByUser || fatal) {
        this.handlers.onStatus('closed', { code: event.code, reason: event.reason });
        return;
      }
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      /* onclose가 뒤따라 호출됨 */
    };
  }

  private scheduleReconnect(): void {
    this.attempts += 1;
    const delay = Math.min(MAX_BACKOFF_MS, 500 * 2 ** Math.min(this.attempts, 5)) + Math.random() * 300;
    this.handlers.onStatus('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      if (!this.closedByUser) this.open();
    }, delay);
  }

  private cleanupTimers(): void {
    if (this.keepalive) clearInterval(this.keepalive);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.keepalive = null;
    this.reconnectTimer = null;
  }
}
